import { Button } from "@screencloud/screencloud-ui-components";
import { FormattedMessage } from "react-intl";
import { formatted } from "src/billinglatest/util/currency";
import darkPixiIcon from "src/pages/Welcome/images/dark-pixi-icon.svg";
import type { PixiWizardSelection } from "../../hooks/usePixiWizardState";
import { MOCK_BILLING_ADDRESS } from "../CheckoutStep/mockReviewData";
import { useCheckoutPricing } from "../CheckoutStep/useCheckoutPricing";
import { maskEmail } from "./utils/maskEmail";
import {
  CardsColumn,
  CompleteButtonRow,
  CompleteLayout,
  ConfirmRow,
  ConfirmText,
  Divider,
  FreeDeviceLabel,
  GrandTotalLabel,
  GrandTotalRow,
  GrandTotalValue,
  Header,
  Heading,
  MailIcon,
  PixiInlineLogo,
  RowLabel,
  RowValue,
  SubTitle,
  SuccessIcon,
  SummaryCard,
  SummaryRow,
  TotalLabel,
  TotalValue,
} from "./styles";

export interface CompleteStepProps {
  state: PixiWizardSelection;
  /** Exits the wizard (closes the modal). */
  onComplete: () => void;
}

/**
 * The post-purchase success screen (GRWBKK-1663). Renders inside the wizard
 * modal in place of Checkout once the purchase succeeds, summarising the
 * subscription and hardware order. Values come from the shared checkout pricing
 * (repoint to the `createCustomer` response when GRWBKK-1662 lands).
 */
export const CompleteStep = ({ state, onComplete }: CompleteStepProps) => {
  const {
    planContent,
    currency,
    billingCycle,
    licenses,
    isDiscount,
    discountCents,
    shippingCents,
    subscriptionTotalCents,
    grandTotalCents,
  } = useCheckoutPricing(state);

  const price = (cents: number) => formatted(currency, cents);
  const billingAddress = state.billingAddress ?? MOCK_BILLING_ADDRESS;
  const maskedEmail = maskEmail(billingAddress.email);

  return (
    <CompleteLayout data-testid="complete-step">
      <Header>
        <SuccessIcon
          name="checked-circle-fill"
          aria-hidden
          data-testid="complete-success-icon"
        />
        <Heading>
          <FormattedMessage
            id="pixi_wizard.complete.title"
            defaultMessage="Subscription Successful"
          />
        </Heading>
        <SubTitle>
          <FormattedMessage
            id="pixi_wizard.complete.subtitle"
            defaultMessage="Thank you for your subscription. A confirmation email has also been sent to you for hardware order tracking"
          />
        </SubTitle>
      </Header>

      <CardsColumn>
        <SummaryCard data-testid="complete-subscription-summary">
          <SummaryRow>
            <RowLabel>
              <FormattedMessage
                id="pixi_wizard.complete.plan"
                defaultMessage="Plan"
              />
            </RowLabel>
            <RowValue data-testid="complete-plan">{planContent.name}</RowValue>
          </SummaryRow>
          <Divider />
          <SummaryRow>
            <RowLabel>
              <FormattedMessage
                id="pixi_wizard.complete.billing_cycle"
                defaultMessage="Billing Cycle"
              />
            </RowLabel>
            <RowValue data-testid="complete-billing-cycle">
              {billingCycle === "year" ? (
                <FormattedMessage
                  id="pixi_wizard.complete.cycle_annual"
                  defaultMessage="Annual"
                />
              ) : (
                <FormattedMessage
                  id="pixi_wizard.complete.cycle_monthly"
                  defaultMessage="Monthly"
                />
              )}
            </RowValue>
          </SummaryRow>
          <Divider />
          <SummaryRow>
            <RowLabel>
              <FormattedMessage
                id="pixi_wizard.complete.licenses"
                defaultMessage="Number of licenses"
              />
            </RowLabel>
            <RowValue data-testid="complete-licenses">{licenses}</RowValue>
          </SummaryRow>
          <Divider />
          <SummaryRow>
            <TotalLabel>
              <FormattedMessage
                id="pixi_wizard.complete.subscription_total"
                defaultMessage="Subscription total"
              />
            </TotalLabel>
            <TotalValue data-testid="complete-subscription-total">
              {price(subscriptionTotalCents)}
            </TotalValue>
          </SummaryRow>
        </SummaryCard>

        <SummaryCard data-testid="complete-hardware-summary">
          {isDiscount ? (
            <SummaryRow>
              <RowLabel>
                <FormattedMessage
                  id="pixi_wizard.complete.discount"
                  defaultMessage="Discount"
                />
              </RowLabel>
              <RowValue data-testid="complete-discount">
                -{price(discountCents)}
              </RowValue>
            </SummaryRow>
          ) : (
            <>
              <SummaryRow>
                <FreeDeviceLabel data-testid="complete-free-device">
                  <FormattedMessage
                    id="pixi_wizard.complete.free_device"
                    defaultMessage="Free {pixiLogo} device"
                    values={{
                      pixiLogo: (
                        <PixiInlineLogo src={darkPixiIcon} alt="PIXI" />
                      ),
                    }}
                  />
                </FreeDeviceLabel>
                <RowValue data-testid="complete-device-count">
                  {licenses}
                </RowValue>
              </SummaryRow>
              <Divider />
              <SummaryRow>
                <RowLabel>
                  <FormattedMessage
                    id="pixi_wizard.complete.shipping"
                    defaultMessage="Shipping cost"
                  />
                </RowLabel>
                <RowValue data-testid="complete-shipping">
                  {price(shippingCents)}
                </RowValue>
              </SummaryRow>
            </>
          )}
          <Divider />
          <ConfirmRow>
            <MailIcon name="mail" aria-hidden />
            <ConfirmText data-testid="complete-confirmation-email">
              <FormattedMessage
                id="pixi_wizard.complete.confirmation"
                defaultMessage="Order confirmation sent to {email}"
                values={{ email: maskedEmail }}
              />
            </ConfirmText>
          </ConfirmRow>
        </SummaryCard>

        <GrandTotalRow>
          <GrandTotalLabel>
            <FormattedMessage
              id="pixi_wizard.complete.total"
              defaultMessage="Total"
            />
          </GrandTotalLabel>
          <GrandTotalValue data-testid="complete-grand-total">
            {price(grandTotalCents)}
          </GrandTotalValue>
        </GrandTotalRow>
      </CardsColumn>

      <CompleteButtonRow>
        <Button primary onClick={onComplete} data-testid="complete-button">
          <FormattedMessage
            id="pixi_wizard.complete.complete"
            defaultMessage="Complete"
          />
        </Button>
      </CompleteButtonRow>
    </CompleteLayout>
  );
};
